import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Card-shaped section whose body collapses on click. Built on the
 * native <details> / <summary> elements so keyboard navigation, focus
 * outlines, and assistive tech "expand / collapse" announcements come
 * for free. Use this for any panel that's useful but not needed by
 * default on a page (event history, raw metadata, etc.).
 */
export function CollapsibleSection({
  icon,
  title,
  description,
  trailing,
  defaultOpen = false,
  children,
  className = '',
  bodyClassName = 'p-5 sm:p-6',
}: {
  icon?: ReactNode;
  title: ReactNode;
  /**
   * Optional second line under the title. When set, the title becomes a
   * two-line block and the trailing content and chevron centre against
   * it; omit it (the default) and the summary renders exactly as a
   * single-line header. Existing single-line callers pass nothing and
   * are unaffected.
   */
  description?: ReactNode;
  /** Optional content shown on the right of the header (e.g. a count). */
  trailing?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  /** Override the body padding, e.g. '' for a flush, full-width list. */
  bodyClassName?: string;
}) {
  return (
    <details
      open={defaultOpen}
      className={`group overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm ${className}`}
    >
      <summary
        className={[
          'flex cursor-pointer list-none items-center gap-2 px-5 py-3 transition-colors',
          'hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1',
          'group-open:border-b group-open:border-divider/10',
        ].join(' ')}
      >
        {icon}
        {description ? (
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-fg">{title}</h2>
            <p className="mt-0.5 text-xs font-normal text-fg-muted">{description}</p>
          </div>
        ) : (
          <h2 className="text-sm font-semibold text-fg">{title}</h2>
        )}
        {trailing ? (
          <span className={`text-xs text-fg-dim${description ? ' shrink-0' : ''}`}>
            {trailing}
          </span>
        ) : null}
        <ChevronDown
          className="ml-auto h-4 w-4 shrink-0 text-fg-muted transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className={bodyClassName}>{children}</div>
    </details>
  );
}
